import { useState } from "react";
import { MessageCircle, ArrowRight, Loader2, Shield, User } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { login, adminLogin } from "@/lib/chatApi";
import { AVATARS } from "@/lib/avatars";

export default function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("user");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [loading, setLoading] = useState(false);
  const [adminFailed, setAdminFailed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Masukkan nama pengguna dulu ya");
      return;
    }
    setLoading(true);
    try {
      if (mode === "admin") {
        if (!password) {
          toast.error("Password admin wajib diisi");
          setLoading(false);
          return;
        }
        const res = await adminLogin(username.trim(), password);
        onLogin({ username: res.username, avatar, isAdmin: true, token: res.token });
      } else {
        const user = await login(username.trim(), avatar);
        onLogin(user);
      }
    } catch (err) {
      if (mode === "admin") setAdminFailed(true);
      toast.error(mode === "admin" ? "Username atau password admin salah" : "Gagal masuk, coba lagi");
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = mode === "admin";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-950 p-4 relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="max-w-md w-full p-8 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl backdrop-blur-2xl space-y-6 relative z-10"
      >
        <div className="flex flex-col items-center text-center gap-3">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${isAdmin ? "bg-amber-500" : "bg-indigo-600"}`}>
            {isAdmin ? <Shield className="w-7 h-7 text-white" /> : <MessageCircle className="w-7 h-7 text-white" />}
          </div>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-white font-heading">RuangChat Live</h1>
            <p className="text-sm text-slate-400 mt-1">Obrolan terbuka real-time. Semua orang bisa ikut ngobrol.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-slate-800/60 border border-slate-700">
          <button
            data-testid="login-tab-user"
            onClick={() => setMode("user")}
            className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all ${!isAdmin ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
          >
            <User className="w-4 h-4" /> Pengguna
          </button>
          <button
            data-testid="login-tab-admin"
            onClick={() => setMode("admin")}
            className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all ${isAdmin ? "bg-amber-500 text-white" : "text-slate-400 hover:text-white"}`}
          >
            <Shield className="w-4 h-4" /> Admin
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {!isAdmin && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Pilih avatar</label>
              <div className="flex gap-3">
                {AVATARS.map((a, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid="avatar-selector-item"
                    onClick={() => setAvatar(a)}
                    className={`w-14 h-14 rounded-2xl overflow-hidden border-2 transition-all ${avatar === a ? "border-indigo-500 ring-2 ring-indigo-500/40 scale-105" : "border-slate-700 opacity-70 hover:opacity-100"}`}
                  >
                    <img src={a} alt="avatar" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-300 uppercase tracking-wide">{isAdmin ? "Username admin" : "Nama pengguna"}</label>
            <input
              data-testid="username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={isAdmin ? "mis. cs_rina" : "mis. budi_santoso"}
              className="w-full px-4 py-3 rounded-xl bg-slate-800/70 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
            />
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Password</label>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password admin"
                className="w-full px-4 py-3 rounded-xl bg-slate-800/70 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-base"
              />
            </div>
          )}

          <button
            data-testid="login-button"
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-semibold transition-all disabled:opacity-60 shadow-lg ${isAdmin ? "bg-amber-500 hover:bg-amber-400" : "bg-indigo-600 hover:bg-indigo-500"}`}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{isAdmin ? "Masuk sebagai Admin" : "Masuk Ruang Chat"} <ArrowRight className="w-4 h-4" /></>}
          </button>
          {isAdmin && adminFailed && (
            <p data-testid="admin-login-hint" className="text-xs text-amber-300/80 text-center">
              Username atau password admin salah. Akun admin di versi live terpisah dari preview.
            </p>
          )}
        </form>

        <p className="text-center text-xs text-slate-500">
          {isAdmin ? "Admin mengelola room tertentu & pesan tampil dengan lencana Admin." : "Riwayat chat tersimpan utuh selama pakai nama yang sama."}
        </p>
      </motion.div>
    </div>
  );
}
